import gql from 'graphql-tag';
import { useMemo } from 'react';

import { useDownloadCSV } from '@/api/common';
import {
  InstantSearchSortOrder,
  type QueryState,
  type SolQueryParams,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { csvDownloadRowLimit } from '@/constants';
import {
  useSolImmutableQuery,
  useSolInfiniteQuery,
  useSolPrefetchInfiniteQuery,
  useSolQuery,
} from '@/hooks/useSolQuery';

import fetcher from '../Fetcher';
import { fetcherSol } from '../sol-fetcher';
import { type SolDeleteVariablesType } from '../solType';
import getVariablesFromTableParams, {
  getVariablesFromTableParamsNew,
} from '../util/getVariablesFromTableParams';
import {
  type AudienceActivateVariablesType,
  type AudienceCreateResponseType,
  type AudienceCreateType,
  type AudienceCreateVariablesType,
  type AudienceDatasetResponse,
  type AudienceDeactivateVariablesType,
  type AudienceDeleteResponseType,
  type AudienceGQLResponse,
  AudienceJobStatus,
  type AudienceListGQLResponse,
  type AudienceMembershipGQLResponse,
  type AudienceOptionByIdGQLResponseType,
  type AudienceOptionGQLResponseType,
  type AudienceOptionType,
  type AudienceRulesPreviewGQLResponse,
  type AudienceType,
  type AudienceUpdateResponseType,
  type AudienceUpdateType,
  type AudienceUpdateVariablesType,
  type SalesforceAudienceReportMetadataGQLResponse,
} from './audienceType';

const CSV_AUDIENCE_HEADER = 'domain';

export const uploadAudienceFile = async (file: File | Blob): Promise<string> => {
  const name = file instanceof File ? file.name : 'audience.csv';

  const response = await fetcher.requestSol<File | Blob, AudienceDatasetResponse>({
    url: 'audiences/dataset',
    method: 'POST',
    data: file,
    headers: {
      'Content-Type': file.type,
      'Content-Disposition': `attachment; filename="${name}"`,
    },
  });

  return response.data.dataset;
};

export const uploadAudienceDomains = (domains: string[]) => {
  const file = new Blob([[CSV_AUDIENCE_HEADER, ...domains].join('\n')], { type: 'text/csv' });
  return uploadAudienceFile(file);
};

const DEFAULT_FILTER_FIELDS = ['created_by.name', 'description', 'name'];
const AUDIENCE_OPTIONS_QUERY_KEY = 'audienceOptions';

const audienceOptionsQueryFn =
  ({ search, pageSize }: { search: string; pageSize: number }) =>
  ({ pageParam = 0, signal }: { pageParam?: number; signal?: AbortSignal }) => {
    return {
      query: gql`
        query GetAudienceOptions(
          $page: Pagination!
          $sort: [SortParamInput]!
          $filter: [FilterParamInput]!
        ) {
          audiences {
            get(page: $page, sort: $sort, filter: $filter) {
              edges {
                node {
                  id
                  name
                  metrics {
                    size
                  }
                  isTAM: is_default
                  lastJob: last_job {
                    status
                  }
                }
              }
              pageMeta {
                hasNext
              }
            }
          }
        }
      `,
      variables: getVariablesFromTableParams(['name'], {
        page: {
          offset: pageParam * pageSize,
          limit: pageSize,
        },
        sort: {
          direction: InstantSearchSortOrder.ASC,
          field: 'name',
        },
        filter: search,
      }),
      signal,
    };
  };

export const useAudienceOptions = ({
  search = '',
  pageSize = 100,
  prependedOptions,
}: {
  search?: string;
  pageSize?: number;
  prependedOptions?: AudienceOptionType[];
} = {}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<AudienceOptionGQLResponseType>({
      queryKey: [AUDIENCE_OPTIONS_QUERY_KEY, search],
      queryFn: audienceOptionsQueryFn({ search, pageSize }),
      getNextPageParam: (lastPage, pages) => {
        return lastPage.audiences.get.pageMeta.hasNext ? pages.length + 1 : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].audiences.get.pageMeta.hasNext;

  const audienceOptions = useMemo(() => {
    if (!data) {
      return prependedOptions;
    }

    return (prependedOptions || []).concat(
      data.pages
        .map((d) =>
          d.audiences.get.edges.map((edge) => ({
            ...edge.node,
            disabled:
              edge.node.metrics?.size === 0 ||
              edge.node.lastJob?.status === AudienceJobStatus.PROCESSING,
          })),
        )
        .flat()
        .toSorted((a, b) => (a.isTAM === b.isTAM ? 0 : a.isTAM ? -1 : 1)),
    );
  }, [data, prependedOptions]);

  return {
    audienceOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useAudienceOptionsPrefetch = ({ search = '' } = {}) => {
  return useSolPrefetchInfiniteQuery<AudienceOptionGQLResponseType>({
    queryKey: [AUDIENCE_OPTIONS_QUERY_KEY, search],
    queryFn: audienceOptionsQueryFn({ search, pageSize: 100 }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.audiences.get.pageMeta.hasNext ? pages.length + 1 : undefined,
  });
};

export const useAudienceList = (tableParams?: SolQueryParams) => {
  const { data, error, isLoading, mutate } = useSolQuery<AudienceListGQLResponse>(
    {
      query:
        tableParams &&
        gql`
          query GetAudienceList(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
          ) {
            audiences {
              get(page: $page, sort: $sort, filter: $filter) {
                edges {
                  node {
                    id
                    name
                    isTAM: is_default
                    description
                    type
                    activations {
                      provider {
                        id
                        name
                        capabilities {
                          id
                          settings {
                            name
                            stringValue
                          }
                        }
                      }
                      activatedAt
                      activatedBy {
                        id
                        name
                        email
                      }
                      lastActivationJob: last_activation_job {
                        status
                        errors
                        created_at
                      }
                    }
                    lastJob: last_job {
                      status
                      errors
                    }
                    metrics {
                      emails
                      ips
                      members
                      size
                      visits
                    }
                    created_at
                    created_by {
                      id
                      name
                    }
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: getVariablesFromTableParams(DEFAULT_FILTER_FIELDS, tableParams),
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    audienceListData: data?.audiences.get.edges.map((edge) => edge.node),
    totalResults: data?.audiences.get.totalEdges,
    isLoading,
    error,
    mutate,
  };
};

export const useAudienceOptionById = (audienceId?: string) => {
  const { data, error, isLoading, mutate } = useSolQuery<AudienceOptionByIdGQLResponseType>({
    query:
      !!audienceId &&
      gql`
        query GetAudienceOptionById($audienceId: String) {
          audiences {
            getById(id: $audienceId) {
              id
              name
              metrics {
                size
              }
              isTAM: is_default
            }
          }
        }
      `,
    variables: {
      audienceId,
    },
  });

  return {
    audienceOption: data?.audiences.getById,
    error,
    isLoading,
    mutate,
  };
};

export const useAudienceById = (audienceId?: string) => {
  const { data, error, isLoading, mutate } = useSolQuery<AudienceGQLResponse>({
    query:
      !!audienceId &&
      gql`
        query GetAudience($getByIdId: String) {
          audiences {
            getById(id: $getByIdId) {
              id
              description
              activations {
                provider {
                  id
                  name
                }
              }
              lastJob: last_job {
                status
              }
              metrics {
                size
                visits
              }
              isTAM: is_default
              name
              nameIsImmutable: name_is_immutable
              salesforce_url
              hubspot_url
              type
              rulesConfig
            }
          }
        }
      `,
    variables: {
      getByIdId: audienceId,
    },
  });

  const audience = useMemo<AudienceType | undefined>(() => {
    if (!data) {
      return;
    }

    return {
      ...data.audiences.getById,
      rulesConfig: data.audiences.getById.rulesConfig
        ? JSON.parse(data.audiences.getById.rulesConfig)
        : undefined,
    };
  }, [data]);

  return {
    audience,
    error,
    isLoading,
    mutate,
  };
};

export const useAudienceRulesPreview = (rulesConfig?: string) => {
  const { data, error, isLoading } = useSolQuery<AudienceRulesPreviewGQLResponse>({
    query:
      !!rulesConfig &&
      gql`
        query GetAudienceRulesPreview($rulesConfig: String) {
          audiences {
            getPreview(rulesConfig: $rulesConfig) {
              audienceVisits
              audienceEngagedCompanies
              totalEngagedCompanies
              totalVisits
            }
          }
        }
      `,
    variables: {
      rulesConfig,
    },
  });

  return {
    previewData: data?.audiences.getPreview,
    error,
    isLoading,
  };
};

export const mutateAudienceActivate = async (audienceId: string, providerId: string) => {
  const response = await fetcherSol<
    AudienceActivateVariablesType,
    { audiences: { activate: { success: boolean } } }
  >({
    query: gql`
      mutation AudienceActivate($audienceId: String!, $providerIds: [String!]!) {
        audiences {
          activate(id: $audienceId, providerIds: $providerIds) {
            success
          }
        }
      }
    `,
    variables: {
      audienceId,
      providerIds: [providerId],
    },
  });

  return response.audiences.activate.success;
};

export const mutateAudienceDeactivate = async (audienceId: string, providerId: string) => {
  const response = await fetcherSol<
    AudienceDeactivateVariablesType,
    { audiences: { deactivate: { success: boolean } } }
  >({
    query: gql`
      mutation AudienceDeactivate($audienceId: String!, $providerIds: [String!]!) {
        audiences {
          deactivate(id: $audienceId, providerIds: $providerIds) {
            success
          }
        }
      }
    `,
    variables: {
      audienceId,
      providerIds: [providerId],
    },
  });

  return response.audiences.deactivate.success;
};

export const mutateAudienceCreate = async (audience: AudienceCreateType) => {
  const variables: AudienceCreateVariablesType = {
    audience: {
      description: audience.description,
      name: audience.name,
      type: audience.type,
      is_default: audience.isTAM,
    },
  };

  if (audience.type === 'salesforce') {
    variables.audience.salesforce_url = audience.salesforce_url;
  }

  if (audience.type === 'hubspot') {
    variables.audience.hubspot_url = audience.hubspot_url;
  }

  if (audience.type === 'upload') {
    variables.audience.dataset_id = audience.dataset_id;
  }

  if (audience.type === 'rule') {
    variables.audience.rulesConfig = audience.rulesConfig;
  }

  if (audience.type === 'lookalike') {
    variables.audience.lookalikeConfig = audience.lookalikeConfig;
  }

  return await fetcherSol<AudienceCreateVariablesType, AudienceCreateResponseType>({
    query: gql`
      mutation CreateAudience($audience: CreateAudienceInput!) {
        audiences {
          create(audience: $audience) {
            id
            description
            lastJob: last_job {
              status
            }
            metrics {
              size
              visits
            }
            name
            isTAM: is_default
            hubspot_url
            salesforce_url
            type
          }
        }
      }
    `,
    variables,
  });
};

export const mutateAudienceUpdate = async (audience: AudienceUpdateType) => {
  return await fetcherSol<AudienceUpdateVariablesType, AudienceUpdateResponseType>({
    query: gql`
      mutation UpdateAudience($audience: UpdateAudienceInput!) {
        audiences {
          update(audience: $audience) {
            id
            description
            lastJob: last_job {
              status
            }
            metrics {
              size
              visits
            }
            name
            isTAM: is_default
            hubspot_url
            salesforce_url
            type
            rulesConfig
          }
        }
      }
    `,
    variables: {
      audience: {
        id: audience.id,
        description: audience.description,
        name: audience.name,
        rulesConfig: audience.rulesConfig,
      },
    },
  });
};

export const mutateAudienceDelete = async (audienceId: string) => {
  const response = await fetcherSol<SolDeleteVariablesType, AudienceDeleteResponseType>({
    query: gql`
      mutation AudienceDelete($deleteId: String!) {
        audiences {
          delete(id: $deleteId) {
            id
            success
          }
        }
      }
    `,
    variables: {
      deleteId: audienceId,
    },
  });

  return response.audiences.delete.success;
};

export const useAudienceMembership = (
  audienceId: string | undefined,
  membershipQueryState: QueryState | undefined,
) => {
  const tableParams = getSolQueryParamsNewFromQueryState(membershipQueryState);

  const { data, error, isLoading } = useSolQuery<AudienceMembershipGQLResponse>(
    {
      query:
        tableParams &&
        gql`
          query GetByAudienceId(
            $audienceId: String!
            $sort: [SortParamInput]!
            $page: Pagination!
            $searchQuery: String
          ) {
            companies {
              getByAudienceId(
                audienceId: $audienceId
                sort: $sort
                page: $page
                searchQuery: $searchQuery
              ) {
                edges {
                  node {
                    id
                    name
                    tld
                    metrics {
                      visits
                      lastActivityDate
                    }
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        audienceId,
        ...getVariablesFromTableParamsNew(tableParams),
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    audienceMembership: data?.companies.getByAudienceId.edges.map((edge) => edge.node),
    totalResults: data?.companies.getByAudienceId.totalEdges,
    isLoading,
    error,
  };
};

export const useCRMAudienceReportMetadata = (reportURL?: string) => {
  const { data, error, isLoading } =
    useSolImmutableQuery<SalesforceAudienceReportMetadataGQLResponse>(
      {
        query:
          !!reportURL &&
          gql`
            query GetAudienceReportMetadata($url: String!) {
              audiences {
                getAudienceReportMetadata(url: $url) {
                  name
                }
              }
            }
          `,
        variables: {
          url: reportURL,
        },
      },
      { retry: false },
    );

  return {
    reportMetadata: data?.audiences.getAudienceReportMetadata,
    isLoading,
    error,
  };
};

export const useAudienceMembershipCSVDownload = (
  audienceId?: string,
  membershipQueryState?: QueryState,
) => {
  const tableParams = getSolQueryParamsNewFromQueryState(membershipQueryState);

  return useDownloadCSV({
    filename: 'C99_audience-membership',
    query: gql`
      query GetAudienceMembershipCSV(
        $audienceId: String!
        $sort: [SortParamInput]!
        $page: Pagination!
        $searchQuery: String
      ) {
        companies {
          getByAudienceId(
            audienceId: $audienceId
            sort: $sort
            page: $page
            searchQuery: $searchQuery
          ) {
            edges {
              node {
                Name: name
                Domain: tld
                Region: region {
                  Name: name
                }
                Revenue_Range: revRange {
                  Name: name
                }
                Industry: sector {
                  Name: name
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      audienceId,
      ...getVariablesFromTableParamsNew(tableParams),
      page: {
        offset: 0,
        limit: csvDownloadRowLimit,
      },
    },
  });
};
